/*
 * VX Case — Leather Collection: página de produto.
 * Cor + modelo escolhem a variante; a prévia ao vivo e a gravação das iniciais
 * vêm do personalizador do totem. No carrinho, a personalização segue o mesmo
 * formato da garrafa (_sku, _texts), que a produção já lê.
 */
(() => {
  "use strict";

  if (window.lcLeatherProduct) return;
  window.lcLeatherProduct = true;

  const PRINT_API_URL = "https://print-storefront-api.vxcase.com.br";
  const UPLOAD_TIMEOUT_MS = 20000;
  const FONT = "Times New Roman";

  /* Posições de gravação (proporção da imagem da capa), herdadas do totem. */
  const POSITIONS_DEFAULT = [
    { id: "center", label: "Topo", x: 0.5, y: 0.532 },
    { id: "bottom-left", label: "Inferior esquerda", x: 0.384, y: 0.847 },
    { id: "bottom-right", label: "Inferior direita", x: 0.626, y: 0.847 },
  ];
  const POSITIONS_BY_COLOR = {
    "Silver Leather": [
      { id: "middle-left", label: "Central esquerda", x: 0.431, y: 0.517 },
      { id: "bottom-left", label: "Inferior esquerda", x: 0.39, y: 0.77 },
    ],
    "Green Leather": [
      { id: "center", label: "Topo", x: 0.5, y: 0.455 },
      { id: "bottom-left", label: "Inferior esquerda", x: 0.384, y: 0.77 },
      { id: "bottom-right", label: "Inferior direita", x: 0.626, y: 0.77 },
    ],
  };
  const LIGHT_POINTS = ["Green Leather", "Leather Caramel", "Leather Vinho"];

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const init = (root) => {
    const data = JSON.parse(root.querySelector("[data-lc-product-json]").textContent);
    const form = root.querySelector("[data-lc-form]");
    const variantInput = root.querySelector("[data-lc-variant-input]");
    const priceBox = root.querySelector("[data-lc-price]");
    const addButton = root.querySelector("[data-lc-add]");
    const addLabel = root.querySelector("[data-lc-add-label]");
    const errorBox = root.querySelector("[data-lc-error]");
    const colorLabel = root.querySelector("[data-lc-color-label]");
    const modelHint = root.querySelector("[data-lc-model-hint]");
    const optionInputs = Array.from(root.querySelectorAll("[data-lc-option]"));
    const thumbs = root.querySelector("[data-lc-thumbs]");
    const stagePhoto = root.querySelector("[data-lc-stage-photo]");
    const stagePreview = root.querySelector("[data-lc-stage-preview]");
    const canvas = root.querySelector("[data-lc-canvas]");
    const initialsEl = root.querySelector("[data-lc-initials]");
    const pointsEl = root.querySelector("[data-lc-points]");
    const foilsEl = root.querySelector("[data-lc-foils]");
    const initialsInput = root.querySelector("[data-lc-initials-input]");
    const summary = root.querySelector("[data-lc-personalize-summary]");
    const media = Array.from(root.querySelector("[data-lc-media]").content.querySelectorAll("img")).map((img) => ({
      id: img.dataset.id,
      alt: img.dataset.alt || "",
      src: img.dataset.src,
      thumb: img.dataset.thumb,
      mockup: img.dataset.mockup,
    }));

    const mockupImage = new Image();
    mockupImage.crossOrigin = "anonymous";

    let selected = [];
    let variant = null;
    let foil = data.foils[0];
    let position = POSITIONS_DEFAULT[0];
    let pointsTimer = 0;
    let sending = false;

    const currentColor = () => selected[data.colorIndex];
    const findVariant = (options) =>
      data.variants.find((item) => item.options.every((value, index) => value === options[index]));

    /* ---------------- Variante ---------------- */
    const renderPrice = () => {
      if (!variant || !priceBox) return;
      const compare = variant.compareAt ? ` <s>${variant.compareAt}</s>` : "";
      priceBox.innerHTML =
        `<span class="lc-price__pix">${variant.pix}</span>` +
        `<span class="lc-price__badge">5% OFF no Pix</span>` +
        `<span class="lc-price__installments">ou <strong>${variant.price}</strong> em até 8x de ` +
        `<strong>${variant.installment}</strong> sem juros${compare}</span>`;
    };

    const syncInputs = () => {
      optionInputs.forEach((input) => {
        const index = Number(input.dataset.lcOption);
        input.checked = selected[index] === input.value;

        /* Um modelo só fica ativo se existir para a cor escolhida. */
        if (index === data.modelIndex) {
          const options = selected.slice();
          options[index] = input.value;
          const match = findVariant(options);
          input.disabled = !match;
          input.closest("label")?.classList.toggle("is-unavailable", !match || !match.available);
        }
      });
    };

    const applyVariant = ({ updateUrl = true } = {}) => {
      variant = findVariant(selected) || null;

      syncInputs();
      if (colorLabel) colorLabel.textContent = currentColor() || "";
      if (variant) variantInput.value = variant.id;

      const available = Boolean(variant?.available);
      addButton.disabled = !available;
      addLabel.textContent = !variant ? "Escolha o modelo" : available ? "Adicionar ao carrinho" : "Esgotado";
      renderPrice();

      if (updateUrl && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }
    };

    const onOptionChange = (index, value) => {
      const previousColor = currentColor();
      selected[index] = value;

      /* Trocou de cor e o modelo não existe nela: vai para o primeiro disponível da cor. */
      if (index === data.colorIndex && !findVariant(selected)) {
        const fallback =
          data.variants.find((item) => item.options[index] === value && item.available) ||
          data.variants.find((item) => item.options[index] === value);
        if (fallback) selected = fallback.options.slice();
      }

      applyVariant();
      if (currentColor() !== previousColor) renderColorMedia();
    };

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => onOptionChange(Number(input.dataset.lcOption), input.value));
    });

    /* ---------------- Galeria da cor ---------------- */
    const colorMedia = () => {
      const color = currentColor();
      const matches = media.filter((item) => item.alt.includes(` - ${color} - `));
      return matches.length ? matches : media;
    };

    const showPreview = () => {
      stagePhoto.hidden = true;
      stagePreview.hidden = false;
      thumbs.querySelectorAll("button").forEach((button, index) => button.setAttribute("aria-current", String(index === 0)));
    };

    const showPhoto = (item, button) => {
      stagePhoto.src = item.src;
      stagePhoto.alt = item.alt;
      stagePhoto.hidden = false;
      stagePreview.hidden = true;
      thumbs.querySelectorAll("button").forEach((other) => other.setAttribute("aria-current", String(other === button)));
    };

    const renderColorMedia = () => {
      const items = colorMedia();
      /* A foto 3 do totem é a capa de frente, sem iniciais: é a base da prévia. */
      const mockup = items.find((item) => / - foto 3$/.test(item.alt)) || items[0];

      thumbs.replaceChildren();

      const previewItem = document.createElement("li");
      const previewButton = document.createElement("button");
      previewButton.type = "button";
      previewButton.className = "lc-thumb lc-thumb--preview";
      previewButton.setAttribute("aria-label", "Prévia com as suas iniciais");
      previewButton.innerHTML = `<img src="${mockup?.thumb || ""}" alt=""><span>Prévia</span>`;
      previewButton.addEventListener("click", showPreview);
      previewItem.append(previewButton);
      thumbs.append(previewItem);

      items.forEach((item) => {
        if (item === mockup) return;
        const li = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.className = "lc-thumb";
        button.setAttribute("aria-label", item.alt);
        button.innerHTML = `<img src="${item.thumb}" alt="">`;
        button.addEventListener("click", () => showPhoto(item, button));
        li.append(button);
        thumbs.append(li);
      });

      showPreview();
      renderPoints();

      if (mockup) {
        mockupImage.onload = drawMockup;
        mockupImage.src = mockup.mockup;
        if (mockupImage.complete) drawMockup();
      }
    };

    /* ---------------- Personalização ---------------- */
    const drawMockup = () => {
      if (!mockupImage.naturalWidth) return;
      canvas.width = mockupImage.naturalWidth;
      canvas.height = mockupImage.naturalHeight;
      const context = canvas.getContext("2d");
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(mockupImage, 0, 0);
    };

    const initials = () => initialsInput?.value || "";

    const updateInitials = () => {
      const value = initials();
      initialsEl.textContent = value || "AG";
      initialsEl.classList.toggle("is-placeholder", !value);

      if (summary) {
        summary.textContent = value
          ? `${value} · foil ${foil.label.toLowerCase()} · posição ${position.label.toLowerCase()}`
          : "Sem iniciais, a capa vai lisa. Toque na prévia para escolher a posição.";
      }
    };

    const setFoil = (next) => {
      foil = next;
      initialsEl.style.setProperty("--foil-color", foil.color);
      initialsEl.style.setProperty("--foil-highlight", foil.highlight);
      initialsEl.style.setProperty("--foil-shadow", foil.shadow);
      foilsEl.querySelectorAll("button").forEach((button) => {
        const isSelected = button.dataset.foil === foil.id;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", String(isSelected));
      });
      updateInitials();
    };

    const renderFoils = () => {
      data.foils.forEach((item) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "lc-foil";
        button.dataset.foil = item.id;
        button.title = item.label;
        button.setAttribute("aria-label", `Foil ${item.label}`);
        button.style.setProperty("--foil-fallback", item.color);
        button.innerHTML = `<span class="lc-foil__swatch"><img src="${item.image}" alt="" width="40" height="40"></span><span class="lc-foil__label">${item.label}</span>`;
        button.addEventListener("click", () => setFoil(item));
        foilsEl.append(button);
      });
      setFoil(data.foils[0]);
    };

    const showPoints = () => {
      window.clearTimeout(pointsTimer);
      pointsEl.classList.add("is-visible");
      pointsEl.classList.remove("is-idle");
      if (!reducedMotion) pointsTimer = window.setTimeout(() => pointsEl.classList.add("is-idle"), 2400);
    };

    const setPosition = (next, reveal = true) => {
      position = next;
      initialsEl.style.left = `${position.x * 100}%`;
      initialsEl.style.top = `${position.y * 100}%`;
      pointsEl.querySelectorAll("button").forEach((point) => {
        const isSelected = point.dataset.position === position.id;
        point.classList.toggle("is-selected", isSelected);
        point.setAttribute("aria-pressed", String(isSelected));
      });
      updateInitials();
      if (reveal) showPoints();
    };

    const renderPoints = () => {
      const color = currentColor();
      const positions = POSITIONS_BY_COLOR[color] || POSITIONS_DEFAULT;
      const tone = LIGHT_POINTS.includes(color) ? "light" : "dark";

      pointsEl.replaceChildren();
      positions.forEach((item) => {
        const point = document.createElement("button");
        point.type = "button";
        point.className = `lc-point lc-point--${tone}`;
        point.dataset.position = item.id;
        point.style.left = `${item.x * 100}%`;
        point.style.top = `${item.y * 100}%`;
        point.setAttribute("aria-label", `Posição ${item.label.toLowerCase()}`);
        point.addEventListener("click", () => setPosition(item));
        pointsEl.append(point);
      });

      const keep = positions.find((item) => item.id === position.id);
      setPosition(keep || positions[0], false);
      showPoints();
    };

    stagePreview.addEventListener("pointerdown", (event) => {
      if (event.target instanceof Element && event.target.closest(".lc-point")) return;
      showPoints();
    });

    initialsInput?.addEventListener("input", () => {
      initialsInput.value = Array.from(initialsInput.value.toLocaleUpperCase("pt-BR"))
        .filter((character) => /\p{L}/u.test(character))
        .slice(0, 2)
        .join("");
      initialsInput.classList.remove("is-invalid");
      errorBox.hidden = true;
      showPreview();
      updateInitials();
    });

    /* Arte final: capa + iniciais no tamanho real, com o mesmo gradiente da prévia. */
    const renderArt = (text) => {
      const width = mockupImage.naturalWidth;
      const height = mockupImage.naturalHeight;
      if (!width || !canvas.clientWidth) return null;

      const art = document.createElement("canvas");
      art.width = width;
      art.height = height;
      const context = art.getContext("2d");
      context.drawImage(mockupImage, 0, 0, width, height);

      const scale = width / canvas.clientWidth;
      const screenFontSize = parseFloat(window.getComputedStyle(initialsEl).fontSize);
      const fontSize = Math.min(400, Math.max(4, Math.round(screenFontSize * scale)));
      const x = position.x * width;
      const y = position.y * height;

      context.font = `600 ${fontSize}px "${FONT}", Times, serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillStyle = "rgb(255 255 255 / 42%)";
      context.fillText(text, x, y + scale);
      context.fillStyle = "rgb(58 43 4 / 40%)";
      context.fillText(text, x, y - scale);

      const half = context.measureText(text).width / 2;
      const gradient = context.createLinearGradient(x - half, y - fontSize / 2, x + half, y + fontSize / 2);
      gradient.addColorStop(0, foil.highlight);
      gradient.addColorStop(0.36, foil.color);
      gradient.addColorStop(0.56, foil.shadow);
      gradient.addColorStop(0.74, foil.color);
      gradient.addColorStop(1, foil.highlight);
      context.fillStyle = gradient;
      context.fillText(text, x, y);

      try {
        return { image: art.toDataURL("image/png"), fontSize };
      } catch {
        return { image: null, fontSize };
      }
    };

    /*
     * Mesmo fluxo da garrafa e das capas estampadas: a arte final sobe para a API
     * de impressão, que devolve original e miniatura. Se a API falhar, o pedido
     * segue sem a imagem, porque iniciais, foil e posição já vão nos campos.
     */
    const uploadArt = async (image, textProperties) => {
      if (!image) return {};
      try {
        const response = await fetch(`${PRINT_API_URL}/print/upload-base64`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image, productSku: variant.sku, textProperties }),
          signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
        });
        const result = await response.json();
        if (!response.ok || result?.statusCode !== 200) {
          throw new Error(result?.message || `HTTP ${response.status}`);
        }
        return { original: result.data?.original || null, thumb: result.data?.thumb || null };
      } catch (error) {
        console.warn("[Leather Collection] Upload da arte indisponível; seguindo sem imagem.", error);
        return {};
      }
    };

    /*
     * Campos do item no pedido, lidos pelo sistema externo. Segue o padrão
     * Print My Case (_Estampa, _sku, _case_original/_thumb, _texts) e acrescenta
     * a posição da gravação, que só existe na capa de couro.
     */
    const buildProperties = async () => {
      const text = initials();
      if (!text) return {};

      const art = renderArt(text);
      const textProperties = {
        text,
        fontFamily: FONT,
        fontSize: art?.fontSize ?? null,
        color: foil.color.toUpperCase(),
      };
      const { original, thumb } = await uploadArt(art?.image, textProperties);

      const textEntry = {
        ...textProperties,
        foil: foil.label,
        foilId: foil.id,
        position: position.id,
        positionLabel: position.label,
        x: position.x,
        y: position.y,
      };
      if (original) textEntry.preview = original;
      if (thumb) textEntry.thumb = thumb;

      const properties = {
        _Estampa: "Leather Collection",
        _sku: variant.sku || "",
        _texts: JSON.stringify([textEntry]),
        _posicao: position.id,
        Capa: "Personalizada",
        Iniciais: text,
        "Cor do foil": foil.label,
        "Posição da gravação": position.label,
      };
      if (original) properties._case_original = original;
      if (thumb) properties._case_thumb = thumb;

      return properties;
    };

    const showError = (message) => {
      errorBox.textContent = message;
      errorBox.hidden = false;
    };

    const openCart = () => {
      const trigger = document.querySelector(".openCartDrawer");
      if (trigger) trigger.click();
      else window.location.href = "/cart";
    };

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (sending) return;

      if (!variant) {
        if (modelHint) modelHint.hidden = false;
        showError("Escolha o modelo do seu aparelho.");
        return;
      }

      if (data.personalizationRequired && Array.from(initials()).length !== 2) {
        initialsInput?.classList.add("is-invalid");
        initialsInput?.focus();
        showError("Digite duas letras para as iniciais.");
        return;
      }

      sending = true;
      addButton.disabled = true;
      addLabel.textContent = "Adicionando…";
      errorBox.hidden = true;

      try {
        const properties = await buildProperties();
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties }] }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.description || "Não foi possível adicionar ao carrinho.");
        openCart();
      } catch (error) {
        showError(error.message || "Não foi possível adicionar ao carrinho.");
      } finally {
        sending = false;
        applyVariant({ updateUrl: false });
      }
    });

    /* Modelos em ordem cronológica, independente da ordem do cadastro. */
    const modelRank = (name) => {
      const match = name.match(/iPhone\s+(\d+)\s*(.*)/i);
      if (!match) return 9999;
      const suffix = ["", "plus", "pro", "pro max"].indexOf(match[2].trim().toLowerCase());
      return Number(match[1]) * 10 + (suffix < 0 ? 5 : suffix);
    };
    const modelPills = root.querySelector(".lc-pills");
    if (modelPills) {
      Array.from(modelPills.children)
        .sort((a, b) => modelRank(a.textContent.trim()) - modelRank(b.textContent.trim()))
        .forEach((pill) => modelPills.append(pill));
    }

    /* ---------------- Início ---------------- */
    const initialVariant =
      data.variants.find((item) => String(item.id) === variantInput.value) || data.variants[0];
    selected = initialVariant.options.slice();

    renderFoils();
    applyVariant({ updateUrl: false });
    renderColorMedia();
    updateInitials();
  };

  const boot = (scope = document) => {
    scope.querySelectorAll("[data-lc-product]").forEach((root) => {
      if (root.dataset.lcReady) return;
      root.dataset.lcReady = "true";
      init(root);
    });
  };

  boot();
  document.addEventListener("shopify:section:load", (event) => boot(event.target));
})();
